import { useState, useEffect, useCallback } from "react";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import Spinner from "../../ui/Spinner/Spinner";
import { Wallet, Transaction } from "../types";
import { cache, request } from "../../../utils";
import EnterPinModal from "../../ui/modal/EnterPinModal";
import { User as UserType } from "../../../stores/useUserStore";
import Toast from "../../ui/toast/Toast";

import {
  AlertCircle,
  CheckCircle2,
  UserSearch,
  Users,
  X,
  Star,
} from "lucide-react";
import axios from "axios";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: Wallet | null;
  onSuccess: (transaction: Transaction) => void;
  
}

interface Beneficiary {
  id: string;
  name: string;
  email: string;
  flag?: string;
  currencies: string[];
  beneficiaryId?: string;
}

const walletTransfer = async (fields: {
  userId: string;
  beneficiaryWalletId: string;
  amount: number;
  currency: string;
  narration?: string;
}) => {
  const { userId, ...data } = fields;
  const { beneficiaryWalletId, amount, currency } = data;
  const idempotencyKey = `${userId}-${beneficiaryWalletId}-${currency}-${amount}`;
  const response = await request.post({
    url: "/transaction/transfer/wallet",
    headers: { idempotencykey: idempotencyKey },
    data,
  });
  return response.data;
};

const getBeneficiaries = async (currency: string): Promise<Beneficiary[]> => {
  const { data } = await request.get({
    url: `/beneficiaries?currency=${currency}`,
  });
  return data?.beneficiaries ?? [];
};

const saveWalletBeneficiary = async (data: {
  beneficiaryId: string;
  currency: string;
}) => {
  const date = new Date().toISOString();
  const idempotencyKey = `${data.beneficiaryId}-${date}`;
  await request.post({
    url: "/beneficiary",
    data,
    headers: { idempotencykey: idempotencyKey },
  });
};

const deleteBeneficiary = async (beneficiaryId: string) => {
  await request.delete(`/beneficiary/${beneficiaryId}`);
};

const TransferModal = ({ isOpen, onClose, wallet, onSuccess }: Props) => {
  const [step, setStep] = useState<"form" | "summary">("form");
  const [showPinModal, setShowPinModal] = useState(false);

  // form fields
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [displayAmount, setDisplayAmount] = useState("");
  const [narration, setNarration] = useState("");

  // recipient
  const [recipient, setRecipient] = useState<Beneficiary | null>(null);
  const [fetchingRecipient, setFetchingRecipient] = useState(false);
  const [recipientError, setRecipientError] = useState("");

  // beneficiaries
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);
  const [showBeneficiaries, setShowBeneficiaries] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // save as beneficiary
  const [saveAsBeneficiary, setSaveAsBeneficiary] = useState(false);
  const [showSaveOption, setShowSaveOption] = useState(false);

  // general
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ── Load beneficiaries ───────────────────────────────────────────────────
  const loadBeneficiaries = useCallback(async () => {
    if (!wallet?.currency) return;
    setBeneficiariesLoading(true);
    try {
      const list = await getBeneficiaries(wallet.currency);
      setBeneficiaries(list);
    } catch {
      setBeneficiaries([]);
    } finally {
      setBeneficiariesLoading(false);
    }
  }, [wallet?.currency]);

  useEffect(() => {
    if (isOpen && wallet?.currency) loadBeneficiaries();
  }, [isOpen, wallet?.currency]);

  // ── Reset on close ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setStep("form");
      setEmail("");
      setAmount("");
      setDisplayAmount("");
      setNarration("");
      setRecipient(null);
      setRecipientError("");
      setShowBeneficiaries(false);
      setSaveAsBeneficiary(false);
      setShowSaveOption(false);
      setError(null);
      setSuccess(null);
      setShowPinModal(false);
    }
  }, [isOpen]);

  // ── Recipient lookup ─────────────────────────────────────────────────────
  const validateEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const lookupRecipient = useCallback(
    async (emailVal: string) => {
      if (!validateEmail(emailVal)) return;
      setFetchingRecipient(true);
      setRecipientError("");
      setRecipient(null);
      try {
        const { data } = await request.get({
          url: `/wallet/resolve?key=${emailVal}`,
        });
        const ben: Beneficiary = data;

        if (!ben.currencies.includes(wallet?.currency ?? "")) {
          setRecipientError(
            `Recipient does not have a ${wallet?.currency} wallet.`
          );
          return;
        }

        setRecipient(ben);
        const alreadySaved = beneficiaries.some(
          (b) => b.beneficiaryId === ben.id
        );
        setShowSaveOption(!alreadySaved);
      } catch {
        setRecipientError("Recipient not found.");
      } finally {
        setFetchingRecipient(false);
      }
    },
    [wallet?.currency, beneficiaries]
  );

  useEffect(() => {
    if (!email || !validateEmail(email)) {
      setRecipient(null);
      setRecipientError("");
      return;
    }
    const t = setTimeout(() => lookupRecipient(email), 400);
    return () => clearTimeout(t);
  }, [email]);

  // ── Delete beneficiary ───────────────────────────────────────────────────
  const handleDeleteBeneficiary = async (
    e: React.MouseEvent,
    beneficiaryId: string
  ) => {
    e.stopPropagation();
    setDeletingId(beneficiaryId);
    try {
      await deleteBeneficiary(beneficiaryId);
      setBeneficiaries((prev) =>
        prev.filter((b) => b.beneficiaryId !== beneficiaryId)
      );
      const deleted = beneficiaries.find(
        (b) => b.beneficiaryId === beneficiaryId
      );
      if (deleted && deleted.email === email) {
        setEmail("");
        setRecipient(null);
      }
    } catch {
      // silently fail
    } finally {
      setDeletingId(null);
    }
  };

  // ── Amount formatting ────────────────────────────────────────────────────
  const formatWithCommas = (val: string) => {
    const clean = val.replace(/[^\d.]/g, "");
    const parts = clean.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d.]/g, "");
    setAmount(raw);
    setDisplayAmount(formatWithCommas(raw));
  };

  const parsedAmount = parseFloat(amount) || 0;

  const canProceed =
    recipient &&
    parsedAmount > 0 &&
    parsedAmount <= (wallet?.balance ?? 0) &&
    !recipientError;

  // ── Steps ────────────────────────────────────────────────────────────────
  const handleContinue = () => {
    if (!canProceed) return;
    setStep("summary");
  };

  const handleConfirm = () => {
    setShowPinModal(true);
  };

  const handlePinVerified = async (pin: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const user = cache.get("USER") as UserType | null;
      const token = user?.token;

      const validationResponse = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/user/pin/validate`,
        { pin },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // ✅ Fixed: access .data.success, not .success
      if (!validationResponse?.data?.success) {
        setError("Invalid PIN. Please try again.");
        return false;
      }
      const result = await walletTransfer({
  userId: "",
  beneficiaryWalletId: recipient!.id,
  amount: parsedAmount,
  currency: wallet!.currency,
  narration: narration || undefined,
});

      if (saveAsBeneficiary) {
        await saveWalletBeneficiary({
          beneficiaryId: recipient!.id,
          currency: wallet!.currency,
        });
        await loadBeneficiaries();
      }

      // ✅ Show success toast before closing
      setShowPinModal(false);
      setSuccess("Transfer successful! The recipient's wallet has been credited.");
      setTimeout(() => {
        onSuccess(result);
        onClose();
      }, 3000);

      return true;
    } catch {
      setError("Transfer failed. Please try again.");
      setStep("form");
      return false;
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) return null;

  // ── Beneficiaries panel ──────────────────────────────────────────────────
  const BeneficiariesPanel = () => (
    <div className="mb-3 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Saved Beneficiaries
          </span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {beneficiaries.length}
          </span>
        </div>
        <button
          onClick={() => setShowBeneficiaries(false)}
          className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-48 overflow-y-auto">
        {beneficiariesLoading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400 dark:text-gray-500">
            <Spinner className="h-4 w-4" />
            Loading beneficiaries…
          </div>
        ) : beneficiaries.length === 0 ? (
          <div className="flex flex-col items-center gap-1 py-6 text-center">
            <Star className="h-5 w-5 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No saved beneficiaries yet
            </p>
          </div>
        ) : (
          beneficiaries.map((b) => (
            <div
              key={b.beneficiaryId}
              className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10">
                {b.flag ? (
                  <img
                    src={b.flag}
                    alt={b.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-bold text-primary">
                    {b.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <button
                className="min-w-0 flex-1 text-left"
                onClick={() => {
                  setEmail(b.email);
                  setShowBeneficiaries(false);
                }}
              >
                <p className="truncate text-sm font-medium text-gray-800 dark:text-white">
                  {b.name}
                </p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {b.email}
                </p>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen && !showPinModal}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900"
      >
        {/* ── SUCCESS TOAST ────────────────────────────────────────────────── */}
        {success && (
          <div className="mb-4">
            <Toast
              variant="success"
              title={success}
              message="Your wallet has been debited"
            />
          </div>
        )}

        {/* ── FORM STEP ───────────────────────────────────────────────────── */}
        {step === "form" && (
          <>
            <h2 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
              Transfer {wallet.currency}
            </h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Balance: {wallet.symbol} {wallet.balance.toLocaleString()}
            </p>

            {error && (
              <div className="mb-4">
                <Alert variant="error" title="Error" message={error} />
              </div>
            )}

            {/* Amount */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount ({wallet.symbol})
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                  {wallet.symbol}
                </span>
                <input
                  type="text"
                  value={displayAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 py-3 pl-12 pr-4 text-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              {parsedAmount > (wallet.balance ?? 0) && parsedAmount > 0 && (
                <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  Insufficient balance.
                </div>
              )}
            </div>

            {/* Recipient Email */}
            <div className="mb-5">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Recipient Email
                </label>
                <button
                  onClick={() => setShowBeneficiaries((v) => !v)}
                  className="flex items-center gap-1 text-xs text-gray-600 hover:underline"
                >
                  <UserSearch className="h-3 w-3 text-gray-600" />
                  {showBeneficiaries ? "Hide" : "Beneficiaries"}
                  {beneficiaries.length > 0 && !showBeneficiaries && (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                      {beneficiaries.length}
                    </span>
                  )}
                </button>
              </div>

              {showBeneficiaries && <BeneficiariesPanel />}

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                placeholder="recipient@example.com"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />

              <div className="mt-2 min-h-[20px]">
                {fetchingRecipient && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Spinner className="h-3 w-3" /> Fetching recipient…
                  </div>
                )}
                {!fetchingRecipient && recipient && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4" />
                    {recipient.name}
                  </div>
                )}
                {!fetchingRecipient && recipientError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    {recipientError}
                  </div>
                )}
              </div>
            </div>

            {/* Narration */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Narration{" "}
                <span className="text-gray-400 dark:text-gray-500">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={narration}
                  onChange={(e) => setNarration(e.target.value.slice(0, 100))}
                  placeholder="What's this for?"
                  maxLength={100}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 pr-16 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">
                  {narration.length}/100
                </span>
              </div>
            </div>

            {/* Save as beneficiary toggle */}
            {showSaveOption && recipient && (
              <div className="mb-6 flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Save{" "}
                  <span className="font-medium text-primary">{recipient.name}</span>{" "}
                  as a beneficiary
                </p>
                <button
                  onClick={() => setSaveAsBeneficiary((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    saveAsBeneficiary
                      ? "bg-primary"
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      saveAsBeneficiary ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleContinue}
                disabled={!canProceed}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* ── SUMMARY STEP ────────────────────────────────────────────────── */}
        {step === "summary" && recipient && (
          <>
            <h2 className="mb-6 text-lg font-semibold text-gray-800 dark:text-white/90">
              Confirm Transfer
            </h2>

            <div className="mb-6 space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">To</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {recipient.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Amount</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {wallet.symbol}{" "}
                  {parsedAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              {narration && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Narration
                  </span>
                  <span className="max-w-[60%] text-right text-sm text-gray-700 dark:text-gray-300">
                    {narration}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  From Wallet
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {wallet.name}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setStep("form")}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Enter PIN
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* PIN Modal */}
      <EnterPinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onPinVerified={handlePinVerified}
        title="Confirm Transfer"
        description="Enter your 4-digit PIN to complete this transfer"
        showForgotPin={true}
      />
    </>
  );
};

export default TransferModal;